import React, { useState } from 'react';
import { Share2, Check, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { track } from '../lib/analytics';

/**
 * One-tap share button for MINDESIGN sections.
 * - Mobile (Web Share API): native OS share sheet
 * - Desktop (no share API): copies the deep-link to clipboard + toast
 *
 * Props:
 *   sectionId    string  — anchor id on the page (e.g., "radio", "comics")
 *   sectionTitle string  — friendly label for the share dialog ("MINDESIGN Radio")
 *   description  string  — optional short text for the share payload
 *   variant      "pill" | "ghost"  default "pill"
 */
export const ShareButton = ({
  sectionId,
  sectionTitle,
  description,
  variant = 'pill',
}) => {
  const [copied, setCopied] = useState(false);

  const buildUrl = () => {
    if (typeof window === 'undefined') return '';
    const origin = window.location.origin;
    return sectionId ? `${origin}/#${sectionId}` : origin;
  };

  const handleShare = async () => {
    const url = buildUrl();
    const shareData = {
      title: sectionTitle
        ? `${sectionTitle} · MINDESIGN ENTERPRISES`
        : 'MINDESIGN ENTERPRISES',
      text:
        description ||
        'A transmedia powerhouse — 150+ songs, 30+ stories, 24/7 MINDESIGN Radio, comics, films, and the Four Pillars.',
      url,
    };

    track('share_clicked', { section: sectionId || 'home' });

    // Native share sheet (mobile + some desktop browsers)
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        toast.success(`Shared ${sectionTitle || 'MINDESIGN'} 🚀`);
        return;
      } catch (err) {
        // User cancelled — silent
        if (err?.name === 'AbortError') return;
        // Fall through to clipboard fallback on other errors
      }
    }

    // Clipboard fallback
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied — go paste it anywhere.', {
        description: url,
      });
      setTimeout(() => setCopied(false), 2200);
    } catch (err) {
      toast.error('Could not copy. Long-press the URL to share manually.');
    }
  };

  const baseClasses =
    'inline-flex items-center gap-2 transition-all duration-300 font-bold tracking-[0.18em] uppercase text-xs';

  const variantClasses =
    variant === 'ghost'
      ? 'text-steel-400 hover:text-accent-red px-3 py-2'
      : 'px-5 py-2.5 rounded-full bg-gunmetal-800/70 hover:bg-gunmetal-700 text-steel-200 hover:text-accent-red border border-steel-700/40 hover:border-accent-red/60 backdrop-blur-sm hover:shadow-lg hover:shadow-accent-red/20';

  return (
    <button
      type="button"
      onClick={handleShare}
      className={`${baseClasses} ${variantClasses}`}
      data-testid={`share-btn-${sectionId || 'home'}`}
      aria-label={`Share ${sectionTitle || 'MINDESIGN ENTERPRISES'}`}
    >
      {copied ? (
        <>
          <Check size={14} className="text-green-400" />
          Copied
        </>
      ) : (
        <>
          {navigator?.share ? <Share2 size={14} /> : <Copy size={14} />}
          Share This
        </>
      )}
    </button>
  );
};

export default ShareButton;
